/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.controlsElement = null;
    this.originalSlides = null;
    this.preservedNodes = null;
    this.isInitialized = false;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();
    if (!this.shouldBeActive()) {
      this.setControlsVisibility(this.findControlsElement(), false);
    }

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
      this.updateControlsVisibility();
    }, 150);
  }

  /**
   * Overlay navigation controls are often placed in a sibling block (e.g. a zero-height
   * element after the slider). Mount them on the slider so vertical centering matches
   * the slide area, not the surrounding stack/section.
   */
  mountOverlayControls(controlsElement) {
    if (!controlsElement?.querySelector('.slider-nav-hover')) return;

    if (!this.controlsMount) {
      this.controlsMount = {
        parent: controlsElement.parentElement,
        nextSibling: controlsElement.nextSibling,
      };
    }

    this.element.appendChild(controlsElement);
    controlsElement.classList.add(
      'absolute',
      'inset-0',
      'z-10',
      'pointer-events-none'
    );
  }

  unmountOverlayControls(controlsElement) {
    if (!controlsElement || !this.controlsMount) return;

    controlsElement.classList.remove(
      'absolute',
      'inset-0',
      'z-10',
      'pointer-events-none'
    );

    const { parent, nextSibling } = this.controlsMount;
    if (parent) {
      parent.insertBefore(controlsElement, nextSibling);
    }

    this.controlsMount = null;
  }

  /**
   * Finds controls either nested in the slider or linked from the same section.
   */
  findControlsElement() {
    const blockId = this.element.dataset.blockId;
    let controlsElement = this.element.querySelector('[data-slider-control]');

    if (!controlsElement && blockId) {
      // Look for controls in the same section that target this slider
      const section = this.element.closest('[id^="shopify-section-"]');
      if (section) {
        controlsElement = section.querySelector(`[data-slider-control][data-slider-id="${blockId}"]`);
      }
      
      // If still not found, try any controls without a specific slider ID in the section
      if (!controlsElement) {
        const allControls = section?.querySelectorAll('[data-slider-control]:not([data-slider-id])');
        if (allControls && allControls.length === 1) {
          // Only auto-match if there's exactly one slider and one unassigned controls block
          const allSliders = section?.querySelectorAll('[data-slider-block]');
          if (allSliders && allSliders.length === 1) {
            controlsElement = allControls[0];
          }
        }
      }
    }

    return controlsElement || null;
  }

  /**
   * Hides only the usable controls. Adjacent navigation wraps the slider content,
   * so hiding the whole control container would also hide the slides.
   *
   * Prefer Tailwind `!hidden` over the HTML `hidden` attribute: nav buttons use
   * `flex`, which overrides unscoped `[hidden] { display: none }` in this theme.
   */
  setControlsVisibility(controlsElement, isVisible) {
    if (!controlsElement) return;

    const shouldHide = !isVisible;
    const containsSliderContent = Boolean(controlsElement.querySelector('.slider-content'));
    controlsElement.dataset.sliderControlsOverflow = isVisible ? 'true' : 'false';

    const applyVisibility = (element) => {
      element.classList.toggle('!hidden', shouldHide);
      element.toggleAttribute('hidden', shouldHide);
      element.setAttribute('aria-hidden', String(shouldHide));
    };

    if (!containsSliderContent) {
      applyVisibility(controlsElement);
      return;
    }

    controlsElement
      .querySelectorAll('.slider-navigation-prev, .slider-navigation-next, .swiper-pagination')
      .forEach(applyVisibility);
  }

  /**
   * Controls are useful only when there are more slides than the current view.
   */
  updateControlsVisibility() {
    const controlsElement = this.controlsElement || this.findControlsElement();
    if (!controlsElement) return;

    this.controlsElement = controlsElement;

    if (!this.swiper || !this.originalSlides?.length) {
      this.setControlsVisibility(controlsElement, false);
      return;
    }

    const slidesPerView = this.swiper.params?.slidesPerView;
    let canMove = false;

    if (slidesPerView === 'auto') {
      canMove = this.swiper.snapGrid?.length > 1;
    } else {
      const visibleSlides = parseFloat(slidesPerView);
      canMove = Number.isFinite(visibleSlides)
        ? this.originalSlides.length > visibleSlides
        : this.swiper.snapGrid?.length > 1;
    }

    this.setControlsVisibility(controlsElement, canMove);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) return;

    // Find and preserve control elements before clearing
    // First try within the slider (nested), then try in parent section (separate blocks)
    const blockId = this.element.dataset.blockId;
    const controlsElement = this.findControlsElement();
    this.controlsElement = controlsElement;

    let slides;
    const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );
      
      this.originalSlides = slides;
      // Keep block {% style %} tags — they hold background images / heights for slides
      this.preservedNodes = [...this.contentEl.children].filter(el =>
        excludedTags.includes(el.tagName)
      );
    }

    if (slides.length < 1) {
      this.setControlsVisibility(controlsElement, false);
      return;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      // Firefox honours only the HTML draggable attribute, not the CSS
      // -webkit-user-drag rule, so explicitly disable native drag on
      // links/images to stop them being grabbed during a swipe.
      slide.querySelectorAll('a, img').forEach((el) => {
        el.setAttribute('draggable', 'false');
      });
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper, but keep style/script nodes from nested blocks
    this.contentEl.innerHTML = '';
    (this.preservedNodes || []).forEach(node => this.contentEl.appendChild(node));
    this.contentEl.appendChild(swiperEl);
    
    // Re-append controls if they were nested inside the slider, or mount external
    // overlay navigation controls so they center on the slider itself.
    const wasNested = controlsElement?.parentElement === this.contentEl;
    const isOverlayNavigation = Boolean(controlsElement?.querySelector('.slider-nav-hover'));

    if (controlsElement && wasNested) {
      this.element.appendChild(controlsElement);
    } else if (controlsElement && isOverlayNavigation) {
      this.mountOverlayControls(controlsElement);
    }

    // Get options from data attribute
    const customOptions = this.element.dataset.swiperOptions 
      ? JSON.parse(this.element.dataset.swiperOptions) 
      : {};

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
      // Enable touch/drag interactions
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Improve touch responsiveness
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
      preventClicks: false,
      preventClicksPropagation: false,
      noSwipingClass: 'swiper-no-swiping',
    };

    const customPaginationColour = this.element.dataset.paginationColour;

    // Only enable loop if more than 1 slide
    if (slides.length > 1 && customOptions.loop === true) {
      defaultOptions.loop = true;
    }

    // Add navigation selectors (blockId already defined earlier)
    if (blockId && customOptions.navigation !== false) {
      let navSelector;
      
      if (controlsElement) {
        // Controls exist - use them for navigation
        if (controlsElement.dataset.sliderId === blockId) {
          // Controls are separate and explicitly linked
          navSelector = `[data-slider-control][data-slider-id="${blockId}"]`;
        } else if (!controlsElement.dataset.sliderId) {
          // Controls are nested or auto-matched, use the controls element directly
          // Don't use a selector string, pass the actual element's navigation buttons
          const nextBtn = controlsElement.querySelector('.slider-navigation-next');
          const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
          if (nextBtn && prevBtn) {
            customOptions.navigation = {
              nextEl: nextBtn,
              prevEl: prevBtn,
            };
          }
        }
      }
      
      // If navSelector is set (separate controls), use selector strings
      if (navSelector) {
        customOptions.navigation = {
          nextEl: `${navSelector} .slider-navigation-next`,
          prevEl: `${navSelector} .slider-navigation-prev`,
        };
      }
    }

    // Check if slider-controls block is present (already found earlier)
    let controlsPaginationColour = customPaginationColour;
    
    if (controlsElement) {
      // Find pagination element
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        // Get pagination type and colour from data attributes
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        controlsPaginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        // Override pagination config
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        // Handle custom pagination type - render as styled bars
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = (swiper, current, total) => {
            let bullets = '';
            for (let i = 1; i <= total; i++) {
              const isActive = i === current ? 'opacity-100' : 'opacity-50';
              bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
            }
            return `<div class="flex gap-2 justify-center">${bullets}</div>`;
          };

          // Make custom bullets clickable
          this.element.addEventListener('click', (e) => {
            const bullet = e.target.closest('[data-slide-index]');
            if (bullet && this.swiper) {
              const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
              this.swiper.slideTo(index);
            }
          });
        }
      }
    } else {
      // Fallback: Handle custom pagination type when no controls block (old behavior)
      if (customOptions.pagination && customOptions.pagination.type === 'custom') {
        customOptions.pagination.renderCustom = (swiper, current, total) => {
          let bullets = '';
          for (let i = 1; i <= total; i++) {
            const isActive = i === current ? 'opacity-100' : 'opacity-50';
            bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${customPaginationColour};" data-slide-index="${i}"></span>`;
          }
          return `<div class="flex gap-2 justify-center">${bullets}</div>`;
        };

        // Make custom bullets clickable
        this.element.addEventListener('click', (e) => {
          const bullet = e.target.closest('[data-slide-index]');
          if (bullet && this.swiper) {
            const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
            this.swiper.slideTo(index);
          }
        });
      }
    }

    // Initialize Swiper (loop can throw if too few slides vs slidesPerView — retry without loop)
    const merged = { ...defaultOptions, ...customOptions };
    try {
      this.swiper = new Swiper(swiperEl, merged);
    } catch (err) {
      if (document.body?.dataset?.environment === "development") {
        console.warn("[SliderBlock] Swiper init failed, retrying without loop:", err);
      }
      const fallback = { ...merged, loop: false };
      try {
        this.swiper = new Swiper(swiperEl, fallback);
      } catch (err2) {
        if (document.body?.dataset?.environment === "development") {
          console.error("[SliderBlock] Swiper init failed:", err2);
        }
        return;
      }
    }

    this.isInitialized = true;
    this.updateControlsVisibility();
    ['resize', 'breakpoint', 'update', 'slidesLengthChange', 'observerUpdate'].forEach((eventName) => {
      this.swiper.on(eventName, () => this.updateControlsVisibility());
    });
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      (this.preservedNodes || []).forEach(node => this.contentEl.appendChild(node));
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.isInitialized = false;
    this.unmountOverlayControls(this.controlsElement);
    this.setControlsVisibility(this.controlsElement, false);
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-slider-block]');
  sliders.forEach((slider) => {
    // Marquee reuses `data-slider-block` but slides live in `.marquee-content` (see blocks/marquee.liquid).
    if (slider.querySelector('.marquee-content')) return;
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Export for potential external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;

